import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer } from 'antd';
import useTranslation from '@hooks/useTranslation';
import { 
  selectSelectedPrice, 
  selectCheckoutDrawerVisible, 
  closeCheckoutDrawerAction,
  selectCheckoutIsProcessing
} from '@src/app/reducers/checkout/checkoutSlice';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import globals from '@common/globals';
import checkoutApi, { InitCheckoutIntentResponse } from '@src/api/checkout/checkoutApi';
import StripeCheckoutForm from '../StripeCheckoutForm';
import ThemeProvider from '@common/theme/ThemeProvider';
import CheckoutDrawerSkeleton from './CheckoutDrawerSkeleton/CheckoutDrawerSkeleton';

/**
 * Drawer component for checkout flow that opens from the right
 * @returns React.JSX.Element
 */
const CheckoutDrawer = () => {
  const t = useTranslation('Checkout.Drawer');
  const dispatch = useDispatch();
  const selectedPrice = useSelector(selectSelectedPrice);
  const visible = useSelector(selectCheckoutDrawerVisible);
  const isProcessing = useSelector(selectCheckoutIsProcessing);
  const [stripePromise] = React.useState<Promise<Stripe | null>>(() => loadStripe(globals.STRIPE_PK));
  const [intentData, setIntentData] = React.useState<InitCheckoutIntentResponse | null>(null);
  const [intentError, setIntentError] = React.useState<string | null>(null);
  const [creatingIntent, setCreatingIntent] = React.useState<boolean>(false);
  const [isDeletingPaymentMethod, setIsDeletingPaymentMethod] = React.useState<boolean>(false);

  const closeDrawer = () => dispatch(closeCheckoutDrawerAction());

  /**
   * Handles deletion of a payment method and updates the local state
   * @param paymentMethodId - The ID of the payment method to delete
   */
  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    if (!intentData) { return; }
    
    try {
      setIsDeletingPaymentMethod(true);
      const response = await checkoutApi.deletePaymentMethod(paymentMethodId);
      
      // Update the intent data with the new payment methods list
      setIntentData({
        ...intentData,
        paymentMethods: response.paymentMethods,
      });
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      throw error; // Re-throw to let StripeCheckoutForm handle the error display
    } finally {
      setIsDeletingPaymentMethod(false);
    }
  };

  useEffect(() => {
    const initIntent = async () => {
      if (!visible || !selectedPrice) { return; }
      setCreatingIntent(true);
      setIntentError(null);
      try {
        const res = await checkoutApi.createCheckoutIntent({ priceId: selectedPrice.priceId, quantity: 1 });
        setIntentData(res);
      } catch (e: any) {
        setIntentError(e?.response?.data?.message || e?.message || 'Failed to initialize payment');
      } finally {
        setCreatingIntent(false);
      }
    };
    initIntent();
  }, [visible, selectedPrice]);

  const renderContent = () => {
    if (creatingIntent) {
      return <CheckoutDrawerSkeleton />;
    }
    
    if (intentError) {
      return <div style={{ color: 'red' }}>{intentError}</div>;
    }
    
    if (!intentData) {
      return null;
    }

    // If clientSecret exists, wrap with Elements, otherwise render directly
    if (intentData.clientSecret) {
      return (
        <Elements stripe={stripePromise} options={{ clientSecret: intentData.clientSecret }}>
          <StripeCheckoutForm 
            currentPlanInfo={intentData.currentPlanInfo}
            priceInfo={intentData.priceInfo} 
            paymentMethods={intentData.paymentMethods}
            onClose={closeDrawer}
            onDeletePaymentMethod={handleDeletePaymentMethod}
          />
        </Elements>
      );
    }

    return (
      <StripeCheckoutForm 
        currentPlanInfo={intentData.currentPlanInfo}
        priceInfo={intentData.priceInfo} 
        paymentMethods={intentData.paymentMethods}
        onClose={closeDrawer}
        onDeletePaymentMethod={handleDeletePaymentMethod}
      />
    );
  };

  return (
    <Drawer
      title={t('Title')}
      placement="right"
      width={600}
      closable={!isProcessing && !isDeletingPaymentMethod}
      open={visible}
      onClose={closeDrawer}
      footer={null}
      destroyOnClose
      maskClosable={false}
    >
      <ThemeProvider>
        {renderContent()}
      </ThemeProvider>
    </Drawer>
  );
};

export default CheckoutDrawer;
